/**
 * TaskIndicator Component
 *
 * Displays a visual indicator for task status on calendar events.
 * - Open tasks: colored circle with open count number
 * - All completed: green checkmark-circle icon
 * - No tasks (null): renders nothing
 */

import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, View } from 'react-native';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskCountResult } from '@/lib/utils/TaskCountResolver';

type TaskIndicatorSize = 'small' | 'medium' | 'large';

type TaskIndicatorProps = {
  taskCount: TaskCountResult | null;
  size?: TaskIndicatorSize;
};

const SIZE_PRESETS: Record<TaskIndicatorSize, { circle: number; fontSize: number; iconSize: number }> = {
  small: { circle: 14, fontSize: 9, iconSize: 14 },
  medium: { circle: 18, fontSize: 11, iconSize: 18 },
  large: { circle: 22, fontSize: 13, iconSize: 22 },
};

export function TaskIndicator({ taskCount, size = 'medium' }: TaskIndicatorProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (!taskCount) return null;

  const preset = SIZE_PRESETS[size];

  if (taskCount.allCompleted) {
    return (
      <Ionicons
        name="checkmark-circle"
        size={preset.iconSize}
        color={palette.primary}
      />
    );
  }

  return (
    <View
      style={[
        styles.circle,
        {
          width: preset.circle,
          height: preset.circle,
          borderRadius: preset.circle / 2,
          borderColor: palette.primary,
          backgroundColor: palette.primary,
        },
      ]}
    >
      <Text
        style={[
          styles.countText,
          { fontSize: preset.fontSize, color: colorScheme === 'dark' ? '#000000' : '#FFFFFF' },
        ]}
      >
        {taskCount.openCount}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  circle: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
  },
  countText: {
    fontWeight: '700',
    textAlign: 'center',
  },
});
